import React from 'react';
import { motion } from 'framer-motion';
import { Mic, Youtube, Music2, ArrowRight, Instagram } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a027839a-8849-4776-a8ba-c961dd10b69f/artifacts/co2pjy4b_IMG_20260323_062123.jpg";
const YOUTUBE_URL = "https://youtube.com/@letsgetintoitfbe?si=lyzA8U32o3csvxTK";
const SPOTIFY_URL = "https://open.spotify.com/show/6B1NRfylqvaSDTedOPD3iz?si=RGWDLBCTQgOY7Hoh1b-mCg";
const INSTAGRAM_URL = "https://www.instagram.com/letsgetintoitfbe";
const TIKTOK_URL = "https://www.tiktok.com/@letsgetintoitfbe";

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-16 md:pt-20 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1594488198893-801bd027138a?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/50 to-[#0A0A0A]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-5"
            >
              <div className="relative">
                <div className="absolute -left-4 top-0 bottom-0 w-1 bg-[#FF2A2A]" />
                <img
                  src={LOGO_URL}
                  alt="Let's Get In To It"
                  data-testid="about-logo"
                  className="w-full max-w-md aspect-square object-cover border border-white/5 shadow-2xl"
                />
              </div>
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-7 space-y-6"
            >
              <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A]">
                ABOUT THE SHOW
              </p>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-none">
                THE PODCAST
              </h1>
              <div className="space-y-4 font-body text-base text-[#A1A1AA] leading-relaxed">
                <p>
                  <strong className="text-white">Let's Get In To It</strong> is hosted by <strong className="text-white">Tone</strong> (<a href="https://www.instagram.com/tonemeezy" target="_blank" rel="noopener noreferrer" className="text-[#FF2A2A] hover:underline">@tonemeezy</a>) and <strong className="text-white">Shizz</strong> (<a href="https://www.instagram.com/shizzygetbizzy" target="_blank" rel="noopener noreferrer" className="text-[#FF2A2A] hover:underline">@shizzygetbizzy</a>). 
                  Two guys from Philly discussing growing up, getting older, and how life really be drawlin'!
                </p>
                <p>
                  No scripts. No holding back. Just real talk, unfiltered opinions, and raw conversations that hit different. 
                  Every episode dives deep into the topics that matter - from culture and current events to personal stories and hot takes.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Bring */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-3">
              WHAT WE BRING
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight uppercase">
              THE VIBES
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: <Mic size={28} />,
                title: "REAL TALK",
                desc: "Unscripted, unfiltered conversations that keep it authentic. We say what needs to be said."
              },
              {
                icon: <Youtube size={28} />,
                title: "VIDEO & AUDIO",
                desc: "Watch the full experience on YouTube or catch the audio on Spotify. Your choice, your vibe."
              },
              {
                icon: <Music2 size={28} />,
                title: "WEEKLY DROPS",
                desc: "New episodes dropping regularly. Never miss a beat - subscribe and turn on notifications."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-[#141414] border border-white/5 p-8 hover:border-[#FF2A2A]/30 transition-all duration-300 group"
                data-testid={`about-feature-${idx}`}
              >
                <div className="w-12 h-12 bg-[#FF2A2A]/10 flex items-center justify-center mb-5 text-[#FF2A2A] group-hover:bg-[#FF2A2A]/20 transition-colors">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl uppercase mb-3">{item.title}</h3>
                <p className="font-body text-sm text-[#A1A1AA] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase mb-6">
              DON'T MISS OUT
            </h2>
            <p className="font-body text-base text-[#A1A1AA] max-w-lg mx-auto mb-10">
              Subscribe on your favorite platform and join the conversation.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-youtube-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E60000] transition-all hover:-translate-y-1 shadow-[4px_4px_0px_rgba(255,255,255,0.1)]"
              >
                <Youtube size={18} /> YOUTUBE <ArrowRight size={16} />
              </a>
              <a
                href={SPOTIFY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-spotify-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white font-body font-bold uppercase tracking-wide px-8 py-4 hover:border-[#1DB954] hover:text-[#1DB954] transition-all hover:-translate-y-1"
              >
                <Music2 size={18} /> SPOTIFY <ArrowRight size={16} />
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-instagram-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white font-body font-bold uppercase tracking-wide px-8 py-4 hover:border-[#E1306C] hover:text-[#E1306C] transition-all hover:-translate-y-1"
              >
                <Instagram size={18} /> INSTAGRAM <ArrowRight size={16} />
              </a>
              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-tiktok-cta"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/20 text-white font-body font-bold uppercase tracking-wide px-8 py-4 hover:border-[#00f2ea] hover:text-[#00f2ea] transition-all hover:-translate-y-1"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.87a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.3z"/></svg>
                TIKTOK <ArrowRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
